import { Flex, Text } from '@chakra-ui/react'
import { GetIcon, Icons } from '@ds/Icons'
import { DDSTheme, ddsTokens } from '@ds/index'

import { TEXT_ENUM } from '@/styles/components/text'

// Simplified data structure with optional properties
export interface DisplayTagValue {
	label: string
	operator?: string
	value?: string | string[]
	fieldLabel?: string
	color?: string
	icon?: Icons
}

interface DisplayTagProps {
	value: DisplayTagValue
	onRemove: () => void
}

export const DisplayTag: React.FC<DisplayTagProps> = ({ value, onRemove }) => {
	const tokens = ddsTokens[DDSTheme.light]

	// Use custom icon if provided, otherwise use default tag icon
	const icon = value.icon || Icons.tag

	// If value is provided, format it appropriately
	const formatValue = () => {
		if (!value.value) return null

		if (Array.isArray(value.value)) {
			return value.value.join(', ')
		}

		return value.value.toString()
	}

	// Default to primary color if no color is provided
	const iconColor = value.color || tokens.primitive.light.primary[500]

	return (
		<Flex
			alignItems="center"
			h="28px"
			borderRadius="6px"
			bg={tokens.primitive.light.gray[50]}
			border={`1px solid ${tokens.primitive.light.gray[100]}`}
		>
			<Flex
				h="full"
				gap={1}
				alignItems="center"
				px={2}
				py={0}
				borderRight={
					value.operator || value.value
						? `1px solid ${tokens.primitive.light.gray[100]}`
						: 'none'
				}
			>
				<GetIcon icon={icon} boxSize={4} color={iconColor} />
				<Text variant={TEXT_ENUM.medium14}>
					{value.fieldLabel || value.label}
				</Text>
			</Flex>

			{/* Only render operator section if operator exists */}
			{value.operator && (
				<Flex
					h="full"
					px={2}
					alignItems="center"
					borderRight={
						value.value
							? `1px solid ${tokens.primitive.light.gray[100]}`
							: 'none'
					}
				>
					<Text variant={TEXT_ENUM.medium14}>{value.operator}</Text>
				</Flex>
			)}

			{/* Only render value section if value exists */}
			{value.value && (
				<Flex
					h="full"
					px={2}
					alignItems="center"
					borderRight={`1px solid ${tokens.primitive.light.gray[100]}`}
				>
					<Text variant={TEXT_ENUM.medium14}>{formatValue()}</Text>
				</Flex>
			)}

			<Flex
				h="full"
				px={2}
				alignItems="center"
				justifyContent="center"
				cursor="pointer"
				onClick={onRemove}
				_hover={{
					bg: tokens.primitive.light.gray[100],
					borderRadius: '0 6px 6px 0',
				}}
				transition="background-color 0.2s"
			>
				<GetIcon
					icon={Icons.close}
					boxSize={2.5}
					color={tokens.primitive.light.gray[600]}
					_hover={{
						color: tokens.primitive.light.gray[800],
					}}
				/>
			</Flex>
		</Flex>
	)
}
